/**
 * A porta da barbearia.
 *
 * Quem abre este endereço quase sempre é cliente da casa querendo ver o próprio
 * horário — não alguém decidindo se vai cortar cabelo ali. Por isso a tela abre
 * no login, com a marca da barbearia em cima, e a vitrine (serviços, preços,
 * endereço) fica a um toque de distância, para quem chegou pela bio do
 * Instagram.
 *
 * O catálogo só aparece se a barbearia quiser: há quem faça questão de divulgar
 * o que atende sem tabelar preço na internet.
 */

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { InstallPrompt } from '../components/InstallPrompt.tsx';
import { TenantMark } from '../components/TenantMark.tsx';
import { useTenant } from '../lib/contexts.tsx';
import { duration, phone as formatPhone, money } from '../lib/format.ts';
import { SignInForm } from './SignIn.tsx';

export function Public() {
  const { tenant } = useTenant();
  const [showcase, setShowcase] = useState(false);

  const hasCatalog = tenant.publicCatalog !== 'HIDDEN' && tenant.services.length > 0;
  const hasContact = Boolean(tenant.address || tenant.phone || tenant.instagram);

  return (
    <div className="pb-safe mx-auto flex min-h-full w-full max-w-sm flex-col px-6">
      <header className="pt-safe flex flex-col items-center gap-3 pt-10 pb-6 text-center">
        <TenantMark size={80} />
        <h1 className="text-[26px] leading-tight font-bold text-ink">{tenant.name}</h1>
        {tenant.about && <p className="text-[15px] text-ink-soft">{tenant.about}</p>}
      </header>

      <SignInForm />

      <div className="flex flex-col items-center gap-4 pt-6 pb-6 text-center text-[14px]">
        <Link to="/cadastro" className="font-medium text-accent">
          Ainda não tenho conta
        </Link>

        {(hasCatalog || hasContact) && (
          <button
            type="button"
            onClick={() => setShowcase((open) => !open)}
            className="touch-target text-[14px] text-ink-soft"
          >
            {showcase ? 'Esconder' : hasCatalog ? 'Ver serviços e preços' : 'Ver a barbearia'} ›
          </button>
        )}
      </div>

      <InstallPrompt />

      {showcase && (
        <div className="flex flex-col gap-6 border-t border-line pt-6 pb-10">
          {hasCatalog && <Catalog />}
          {tenant.staff.length > 0 && <Staff />}
          {hasContact && <Contact />}
        </div>
      )}
    </div>
  );
}

function Catalog() {
  const { tenant } = useTenant();
  const showsPrices = tenant.publicCatalog === 'SERVICES_AND_PRICES';

  return (
    <section>
      <h2 className="pb-2 text-[13px] font-semibold tracking-wide text-ink-faint uppercase">
        Serviços
      </h2>
      <ul className="overflow-hidden rounded-2xl border border-line">
        {tenant.services.map((service, index) => (
          <li
            key={service.id}
            className={`flex items-center justify-between gap-3 bg-surface-raised px-4 py-3.5 ${
              index > 0 ? 'border-t border-line' : ''
            }`}
          >
            <div className="min-w-0">
              <p className="truncate text-[15px] font-medium text-ink">{service.name}</p>
              <p className="text-[13px] text-ink-faint">
                a partir de {duration(service.defaultDurationMinutes)}
              </p>
            </div>
            {showsPrices && service.priceCents !== null && (
              <span className="shrink-0 text-[15px] font-semibold text-ink">
                {money(service.priceCents)}
              </span>
            )}
          </li>
        ))}
      </ul>
      <p className="px-1 pt-2 text-[12px] text-ink-faint">
        O tempo de cada atendimento é ajustado para cada cliente — o seu horário sai certo.
      </p>
    </section>
  );
}

function Staff() {
  const { tenant } = useTenant();

  return (
    <section>
      <h2 className="pb-2 text-[13px] font-semibold tracking-wide text-ink-faint uppercase">
        Quem atende
      </h2>
      <div className="flex flex-wrap gap-2">
        {tenant.staff.map((person) => (
          <span
            key={person.id}
            className="rounded-full border border-line bg-surface-raised px-3 py-1.5 text-[14px] text-ink"
          >
            {person.name}
          </span>
        ))}
      </div>
    </section>
  );
}

function Contact() {
  const { tenant } = useTenant();

  return (
    <section className="flex flex-col gap-1 text-[14px] text-ink-soft">
      {tenant.address && <p>{tenant.address}</p>}
      {tenant.phone && <p>{formatPhone(tenant.phone)}</p>}
      {tenant.instagram && (
        <a
          href={`https://instagram.com/${tenant.instagram}`}
          className="text-accent"
          target="_blank"
          rel="noreferrer"
        >
          @{tenant.instagram}
        </a>
      )}
    </section>
  );
}
